import { Injectable, } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from "@angular/router";
import * as Sentry from "@sentry/angular"
import { Observable } from 'rxjs';
import { AppService } from './app.service';
import { AuthService } from "./auth.service";
import { LangsRepository } from './langs.repository';


@Injectable()
export class AuthGuard {
    constructor(
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly appService: AppService,
        private langsRepository: LangsRepository,
    ) { }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        return new Promise((resolve, reject) => {
            this.authService.getMe().toPromise().then(response => {
                if (!response.body || response.body.responseCode !== 200 || !response.body.data) {
                    this.authService.logout();
                    this.authService.prevUrl = state.url;
                    sessionStorage.setItem('oidc-login-retry', state.url);
                    this.router.navigateByUrl("/auth/login");
                    this.appService.showError("Authentication error", response.body ? response.body.error : "Unauthorized", false, response.status);
                    resolve(false);
                    return;
                }

                const serverUser = response.body.data;
                this.authService.user = serverUser;
                // Always persist server user (e.g. after company switch reload) so companyId and other fields stay in sync
                const authToken = this.authService.getToken() || "";
                const authJwt = this.authService.getJwt() || "";
                this.authService.saveAuthData({ token: authToken, jwt: authJwt, user: serverUser });

                if (this.authService!.user!.id) {
                    Sentry.configureScope(scope => {
                      scope.setUser({
                          id: this.authService.user.id.toString(),
                          email: this.authService.user.email,
                          username: this.authService.user.firstName + " " + this.authService.user.lastName,
                      });
                    });
                }
                sessionStorage.removeItem('oidc-login-retry');
                resolve(true);
            }).catch(_err => {
                this.authService.logout();
                this.authService.prevUrl = state.url;

                const isCallbackRoute = state.url.indexOf('/auth/callback') >= 0;
                if (isCallbackRoute) {
                    this.router.navigateByUrl("/auth/login?error=auth_failed");
                    resolve(false);
                    return;
                }

                const retryKey = 'oidc-login-retry';
                const retryValue = sessionStorage.getItem(retryKey);
                if (retryValue === state.url) {
                    this.router.navigateByUrl("/auth/login?error=auth_failed");
                    resolve(false);
                    return;
                }

                sessionStorage.setItem(retryKey, state.url);
                // OIDC-first login: don't show the legacy login page.
                const callbackUrl = this.authService.getOidcCallbackUrl();
                this.authService.startOidcLogin(callbackUrl);

                resolve(false);
            });
        });
    }
}
